import { useRef, useState } from 'react';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import { SxProps, Typography } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReactComponent as ArrowDownIcon } from '../../assets/arrow-down-expand.svg';
import Button from '../Button';
import { Flex } from '../Flex';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { pluralizeString } from '../../utils/common';
import { ComponentSize } from '../../types/types';

const Wrapper = styled(Flex)`
  position: relative;
`;

const CalendarWrapper = styled(Flex)<{
  $menuHorizontalAnchor: 'left' | 'right';
}>`
  background: white;
  position: absolute;
  top: 50px;
  ${({ $menuHorizontalAnchor }) =>
    $menuHorizontalAnchor === 'left' && 'right: 0px;'};
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  z-index: 3;
`;

const DatePickerButtonWrapper = styled(Button)<{
  $size: ComponentSize;
}>`
  width: inherit;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  svg > path {
    fill: ${({ theme }) => theme.palette.common.gray4};
  }

  ${({ $size }) =>
    $size === 'large' &&
    `
    height: 40px;
    padding: 12px 16px;
  `}
`;

type DatePickerButtonProps = {
  date?: Dayjs;
  onClick: () => void;
  size?: ComponentSize;
  disabled?: boolean;
};

const DatePickerButton = ({
  date,
  onClick,
  size = 'medium',
  disabled,
}: DatePickerButtonProps) => {
  const daysBack = dayjs().diff(date, 'days');

  return (
    <DatePickerButtonWrapper
      onClick={onClick}
      variant="outlined"
      color="primary"
      $size={size}
      disabled={disabled}
    >
      <Typography>
        {`Last ${pluralizeString(daysBack, 'day', 'days')}`}
      </Typography>
      <ArrowDownIcon width={16} />
    </DatePickerButtonWrapper>
  );
};

export type Props = {
  sx?: SxProps;
  value?: dayjs.Dayjs;
  onChange: (val?: dayjs.Dayjs) => void;
  menuHorizontalAnchor?: 'left' | 'right';
  minDate?: dayjs.Dayjs | undefined;
  maxDate?: dayjs.Dayjs | undefined;
  size?: ComponentSize;
  disabled?: boolean;
};

const DaysBackPicker = ({
  sx,
  value,
  onChange,
  menuHorizontalAnchor = 'left',
  minDate,
  maxDate = dayjs(),
  size = 'medium',
  disabled,
}: Props) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const calendarRef = useRef<any>();
  useOnClickOutside(calendarRef, () => setIsDatePickerOpen(false));

  return (
    <Wrapper sx={sx}>
      <DatePickerButton
        date={value}
        onClick={() => setIsDatePickerOpen(true)}
        size={size}
        disabled={disabled}
      />

      {isDatePickerOpen && (
        <CalendarWrapper
          ref={calendarRef}
          $menuHorizontalAnchor={menuHorizontalAnchor}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={value}
              disableFuture
              minDate={minDate}
              maxDate={maxDate}
              onChange={(val) => {
                setIsDatePickerOpen(false);
                onChange(val || undefined);
              }}
            />
          </LocalizationProvider>
        </CalendarWrapper>
      )}
    </Wrapper>
  );
};

export default DaysBackPicker;
